import { TFunction } from "i18next"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { deProgressNoteFragment$data } from "@/modules/patients/services/de/__generated__/deProgressNoteFragment.graphql"
import { ExtractNode } from "@/modules/patients/types/ExtractNode"
import { Country } from "@/types/__generated__/union-types"

const CH_PROGRESS_NOTE_DOCUMENT_TYPES: ChDocumentType[] = [
  "sickLeave",
  "generalAttestation",
  "attestationGuardian",
  "psychologicalPsychotherapy",
  "physiotherapy",
  "prescription",
  "referralLetter",
  "ch_nutritionalPrescription",
  "carePlanSummary",
  "measLaboratoryPrescription",
  "ch_measRadiologyPrescription",
  "medicalReportAttachments",
  "ch_neuropsychologicDiagnosticPrescription",
  "ch_laboratoryPrescription",
  "ch_consultationReport",
]

const DE_PROGRESS_NOTE_DOCUMENT_TYPES: DeDocumentType[] = [
  "sickLeave",
  "generalAttestation",
  "attestationGuardian",
  "therapyPrescription",
  "referralLetter",
  "carePlanSummary",
  "prescription",
  "medicalReportAttachments",
]

const CH_LEGAL_GUARDIAN_LIMITED_AGE = 15

const DE_LEGAL_GUARDIAN_LIMITED_AGE = 18

type DocumentViewerType = ChDocumentType | DeDocumentType

const getDocumentTitle = (t: TFunction, type: DocumentViewerType, countryCode: Country): string => {
  switch (type) {
    case "prescription":
      return t("patient.prescription.prescription")
    case "attestationGuardian":
      return t("patient.legalGuardianAttestation.attestationForLegalGuardian")
    case "generalAttestation":
      return t("episodeOfCare.progressNote.generalAttestation")
    case "sickLeave":
      return countryCode === "CH"
        ? t("episodeOfCare.progressNote.sickLeaveCertificate.ch")
        : t("episodeOfCare.progressNote.sickLeaveCertificate.de")
    case "ch_nutritionalPrescription":
      return t("episodeOfCare.progressNote.therapyPrescriptionNutritionalCounselling")
    case "physiotherapy":
      return t("patient.therapyPrescriptionPhysiotherapy.therapyPrescriptionPhysiotherapy")
    case "psychologicalPsychotherapy":
      return t("patient.therapyPrescription.therapyPrescriptionPsychologicalPsychotherapy")
    case "referralLetter":
      return t("episodeOfCare.progressNote.referral")
    case "measLaboratoryPrescription":
      return t("patient.measLaboratoryPrescription.measLaboratoryPrescription")
    case "ch_measRadiologyPrescription":
      return t("patient.measRadiologyPrescription.radiologyPrescriptionMEAS")
    case "medicalReportAttachments":
      return t("episodeOfCare.progressNote.medicalReport")
    case "ch_neuropsychologicDiagnosticPrescription":
      return t("patient.neuropsychologicDiagnosticPrescription.neuropsychologicDiagnosticPrescription")
    case "ch_laboratoryPrescription":
      return t("patient.laboratoryPrescription.prescriptionForMedgateConnect")
    case "ch_consultationReport":
      return t("patient.consultationReport.title")
    case "carePlanSummary":
      return t("episodeOfCare.progressNote.treatmentPlan.headerTitle")
    case "therapyPrescription":
      return t("episodeOfCare.progressNote.deTherapyPrescription")
  }
}

export {
  DE_PROGRESS_NOTE_DOCUMENT_TYPES,
  CH_PROGRESS_NOTE_DOCUMENT_TYPES,
  CH_LEGAL_GUARDIAN_LIMITED_AGE,
  DE_LEGAL_GUARDIAN_LIMITED_AGE,
  type DocumentViewerType,
  getDocumentTitle,
}

export type GenericDocument = {
  id: string
  signedBy: string | null
  type: DocumentType
  concludedBy: string | null
}

export type ChDocumentType = keyof Pick<
  chProgressNoteFragment$data,
  | "sickLeave"
  | "generalAttestation"
  | "attestationGuardian"
  | "psychologicalPsychotherapy"
  | "physiotherapy"
  | "prescription"
  | "referralLetter"
  | "ch_nutritionalPrescription"
  | "carePlanSummary"
  | "measLaboratoryPrescription"
  | "ch_measRadiologyPrescription"
  | "medicalReportAttachments"
  | "ch_neuropsychologicDiagnosticPrescription"
  | "ch_laboratoryPrescription"
  | "ch_consultationReport"
>

export type DeDocumentType = keyof Pick<
  deProgressNoteFragment$data,
  | "sickLeave"
  | "generalAttestation"
  | "attestationGuardian"
  | "therapyPrescription"
  | "referralLetter"
  | "carePlanSummary"
  | "prescription"
  | "medicalReportAttachments"
>

export type DocumentType = ChDocumentType | DeDocumentType
export const CARE_PLAN_SUMMARY = "carePlanSummary"

export const CH_DOCUMENTS: Record<string, ChDocumentType> = {
  PRESCRIPTION: "prescription",
  SICK_LEAVE: "sickLeave",
  GUARDIAN_ATTESTATION: "attestationGuardian",
  PHYSIOTHERAPY: "physiotherapy",
  NUTRITIONAL_PRESCRIPTION: "ch_nutritionalPrescription",
  PSYCHOLOGICAL_PSYCHOTHERAPY: "psychologicalPsychotherapy",
  GENERAL_ATTESTATION: "generalAttestation",
  REFERRAL_LETTER: "referralLetter",
  CARE_PLAN: "carePlanSummary",
  MEAS_LABORATORY_PRESCRIPTION: "measLaboratoryPrescription",
  MEAS_RADIOLOGY_PRESCRIPTION: "ch_measRadiologyPrescription",
  NEUROPSYCHOLOGIC_DIAGNOSTIC_PRESCRIPTION: "ch_neuropsychologicDiagnosticPrescription",
  LABORATORY_PRESCRIPTION: "ch_laboratoryPrescription",
  CONSULTATION_REPORT: "ch_consultationReport",
}

export const DE_DOCUMENTS: Record<string, DeDocumentType> = {
  GUARDIAN_ATTESTATION: "attestationGuardian",
  SICK_LEAVE: "sickLeave",
  THERAPY_PRESCRIPTION: "therapyPrescription",
  GENERAL_ATTESTATION: "generalAttestation",
  REFERRAL_LETTER: "referralLetter",
  CARE_PLAN: "carePlanSummary",
  PRESCRIPTION: "prescription",
}

export const CH_SIGNABLE_DOCUMENTS = [
  "attestationGuardian",
  "sickLeave",
  "physiotherapy",
  "ch_nutritionalPrescription",
  "prescription",
  "psychologicalPsychotherapy",
  "generalAttestation",
  "ch_measRadiologyPrescription",
  "measLaboratoryPrescription",
  "ch_neuropsychologicDiagnosticPrescription",
  "ch_laboratoryPrescription",
]
export const DE_SIGNABLE_DOCUMENTS = [
  "generalAttestation",
  "attestationGuardian",
  "sickLeave",
  "referralLetter",
  "prescription",
  "therapyPrescription",
]

export const QES_DOCUMENTS = ["prescription", "therapyPrescription"]

export const CH_MEAS_DOCUMENTS = ["measLaboratoryPrescription", "ch_measRadiologyPrescription"]

export const DOCS_WITH_CODE = [...CH_MEAS_DOCUMENTS, "ch_laboratoryPrescription"]

export const EXTERNAL_CARE_PROVIDER_DOCUMENTS: (ChDocumentType | DeDocumentType)[] = [
  "referralLetter",
  "ch_consultationReport",
]

export const CH_APPROVABLE_DOCUMENTS = ["carePlanSummary", "referralLetter", "ch_consultationReport"]
export const DE_APPROVABLE_DOCUMENTS = ["carePlanSummary"]

export type AddTypeProperty<T> = T extends null ? T : T & { type: ChDocumentType | DeDocumentType }

export type ChProgressNoteDocument = {
  carePlanSummary:
    | (AddTypeProperty<Partial<CarePlanSummaryDocument>> & {
        id: string
        progressNoteId: string
      })
    | null
  prescription: AddTypeProperty<chProgressNoteFragment$data["prescription"]> | null
  sickLeave: AddTypeProperty<chProgressNoteFragment$data["sickLeave"]> | null
  attestationGuardian: AddTypeProperty<chProgressNoteFragment$data["attestationGuardian"]> | null
  physiotherapy: AddTypeProperty<chProgressNoteFragment$data["physiotherapy"]> | null
  ch_nutritionalPrescription: AddTypeProperty<chProgressNoteFragment$data["ch_nutritionalPrescription"]> | null
  psychologicalPsychotherapy: AddTypeProperty<chProgressNoteFragment$data["psychologicalPsychotherapy"]> | null
  generalAttestation: AddTypeProperty<chProgressNoteFragment$data["generalAttestation"]> | null
  referralLetter: AddTypeProperty<chProgressNoteFragment$data["referralLetter"]> | null
  measLaboratoryPrescription: AddTypeProperty<chProgressNoteFragment$data["measLaboratoryPrescription"]> | null
  ch_measRadiologyPrescription: AddTypeProperty<chProgressNoteFragment$data["ch_measRadiologyPrescription"]> | null
  medicalReportAttachments: AddTypeProperty<Partial<{ attachments: MedicalReportAttachments }>> | null
  ch_neuropsychologicDiagnosticPrescription: AddTypeProperty<
    chProgressNoteFragment$data["ch_neuropsychologicDiagnosticPrescription"]
  > | null
  ch_laboratoryPrescription: AddTypeProperty<chProgressNoteFragment$data["ch_laboratoryPrescription"]> | null
  ch_consultationReport: AddTypeProperty<chProgressNoteFragment$data["ch_consultationReport"]> | null
}

export type DeProgressNoteDocument = {
  carePlanSummary:
    | (AddTypeProperty<Partial<CarePlanSummaryDocument>> & {
        id: string
        progressNoteId: string
      })
    | null
  attestationGuardian: AddTypeProperty<deProgressNoteFragment$data["attestationGuardian"]> | null
  sickLeave: AddTypeProperty<deProgressNoteFragment$data["sickLeave"]> | null
  therapyPrescription: AddTypeProperty<deProgressNoteFragment$data["therapyPrescription"]> | null
  generalAttestation: AddTypeProperty<deProgressNoteFragment$data["generalAttestation"]> | null
  referralLetter: AddTypeProperty<deProgressNoteFragment$data["referralLetter"]> | null
  prescription: AddTypeProperty<deProgressNoteFragment$data["prescription"]> | null
  medicalReportAttachments: AddTypeProperty<Partial<{ attachments: MedicalReportAttachments }>> | null
}

export type AllCountriesFilterableProgressNoteDocument = ChProgressNoteDocument & DeProgressNoteDocument

export type CarePlanSummaryDocument =
  | ExtractNode<chProgressNoteFragment$data["carePlanSummary"]>
  | ExtractNode<deProgressNoteFragment$data["carePlanSummary"]>

export type ProgressNoteDocumentType = ChDocumentType | DeDocumentType | null

export type TelephoneLineType =
  | chProgressNoteFragment$data["appointment"]["telephoneLine"]
  | deProgressNoteFragment$data["appointment"]["telephoneLine"]

export type MedicalReportAttachments =
  | chProgressNoteFragment$data["medicalReportAttachments"]
  | deProgressNoteFragment$data["medicalReportAttachments"]

export type MedicalReportAttachment =
  | ExtractNode<chProgressNoteFragment$data["medicalReportAttachments"]>
  | ExtractNode<deProgressNoteFragment$data["medicalReportAttachments"]>

export type MeasDocumentType = keyof Pick<
  chProgressNoteFragment$data,
  "measLaboratoryPrescription" | "ch_measRadiologyPrescription"
>
